// src/components/Search/SearchSize.jsx

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { SizesIcon, LengthIcon, WidthIcon } from '@/containers/Icons';
import InputAdornment from '@mui/material/InputAdornment';
import {
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { usePreferences } from '@/contexts/ShopPreferences.context';
import { useFilters } from '@/contexts/Filters.context';


// SI tolerance options (in cm)
const SI_TOLERANCE_OPTIONS = [5, 10, 20, 30, 40, 50];
// USC tolerance options (in inches)
const USC_TOLERANCE_OPTIONS = [2, 4, 8, 12, 16, 20];

// Conversion constants
const INCHES_TO_CM = 2.54;
const FEET_TO_CM = 30.48;


export default function SearchSize({ sizesTranslations }) {

  const { unitSystem } = usePreferences();
  const { hasFilter, searchParams, updateFilterRaw } = useFilters();

  const [activeType, setActiveType] = useState('plusMinus'); // 'plusMinus' or 'minMax'

  const [plusMinus, setPlusMinus] = useState({
    plusMinusWidth: '',
    plusMinusLength: '',
    widthTolerance: unitSystem === 'USC' ? 12 : 30, // Default tolerance
    lengthTolerance: unitSystem === 'USC' ? 12 : 30,
  });

  const [minMax, setMinMax] = useState({
    minWidth: '',
    maxWidth: '',
    minLength: '',
    maxLength: '',
  });

  const [_, setRoundOval] = useState({
    diam: '',
    ovalTolerance: unitSystem === 'USC' ? 12 : 30,
  });

  // Use a ref to track if it's the initial render
  const isInitialRender = useRef(true);

  // Simple string value based on unitSystem
  const unitAdornment = unitSystem === 'USC' ? 'ft' : 'cm';

  // Array selection based on unitSystem
  const toleranceOptions = unitSystem === 'USC' ? USC_TOLERANCE_OPTIONS : SI_TOLERANCE_OPTIONS;

  // Simple string value based on unitSystem
  const toleranceLabel = unitSystem === 'USC' ? '+ - in' : '+ -';

  // Convert from display units to cm for storage
  const convertToCm = useCallback((value) => {
    if (!value || value === '') return '';

    if (unitSystem === 'USC') {
      // Convert from feet to cm
      return Math.round(Number(value) * FEET_TO_CM);
    }
    return Number(value);
  }, [unitSystem]);

  // Convert from cm to display units
  const convertFromCm = useCallback((value) => {
    if (!value || value === '') return '';

    if (unitSystem === 'USC') {
      // Convert from cm to feet
      return (Number(value) / FEET_TO_CM).toFixed(2);
    }
    return value;
  }, [unitSystem]);

  // Convert tolerance from display units to cm
  const convertToleranceToCm = useCallback((value) => {
    if (!value || value === '') return '';

    if (unitSystem === 'USC') {
      // Convert from inches to cm
      return Math.round(Number(value) * INCHES_TO_CM);
    }
    return Number(value);
  }, [unitSystem]);

  // Update states when 'size' filter is present or absent
  useEffect(() => {
    if (isInitialRender.current) {
      if (hasFilter('size')) {
        // Get the size parameters from the filter context
        const sizeValue = searchParams.get('size');

        // Determine activeType based on sizeValue format
        const sizeParams = sizeValue.split(',');
        const isMinMax = sizeParams.some((param) =>
          ['minWidth', 'maxWidth', 'minLength', 'maxLength'].includes(
            param.split(':')[0]
          )
        );

        if (isMinMax) {
          setActiveType('minMax');
          const filtersSize = {
            minWidth: '',
            maxWidth: '',
            minLength: '',
            maxLength: '',
          };

          for (const pair of sizeParams) {
            const [key, value] = pair.split(':');
            filtersSize[key] = value;
          }

          // Convert values from cm to display units
          setMinMax({
            minWidth: convertFromCm(filtersSize.minWidth),
            maxWidth: convertFromCm(filtersSize.maxWidth),
            minLength: convertFromCm(filtersSize.minLength),
            maxLength: convertFromCm(filtersSize.maxLength),
          });
        } else {
          setActiveType('plusMinus');
          // Parse plusMinus values if needed
        }
      } else {
        setActiveType('plusMinus');
      }
      isInitialRender.current = false;
    } else {
      // On subsequent renders, if 'size' is not present, clear the fields
      if (!hasFilter('size')) {
        if (activeType === 'minMax') {
          setMinMax({
            minWidth: '',
            maxWidth: '',
            minLength: '',
            maxLength: '',
          });
        } else if (activeType === 'plusMinus') {
          setPlusMinus({
            plusMinusWidth: '',
            plusMinusLength: '',
            widthTolerance: unitSystem === 'USC' ? 12 : 30,
            lengthTolerance: unitSystem === 'USC' ? 12 : 30,
          });
        } else if (activeType === 'roundOval') {
          setRoundOval({
            diam: '',
            ovalTolerance: unitSystem === 'USC' ? 12 : 30,
          });
        }
      }
    }
  }, [searchParams, activeType, convertFromCm, unitSystem, hasFilter]);

  // Effect to update tolerance defaults when unitSystem changes
  useEffect(() => {
    setPlusMinus(prev => ({
      ...prev,
      widthTolerance: unitSystem === 'USC' ? 12 : 30,
      lengthTolerance: unitSystem === 'USC' ? 12 : 30,
    }));

    setRoundOval(prev => ({
      ...prev,
      ovalTolerance: unitSystem === 'USC' ? 12 : 30,
    }));
  }, [unitSystem]);

  // New function to update size filter using the filter context
  const updateSizeFilter = useCallback(
    (sizeObj) => {
      // Filter out empty values
      const filteredSize = Object.fromEntries(
        Object.entries(sizeObj).filter(([, value]) =>
          value !== '' && value.toString().length > 0
        )
      );

      // Convert to 'key:value' format string for the filter
      if (Object.keys(filteredSize).length > 0) {
        const sizeParams = Object.entries(filteredSize)
          .map(([key, value]) => `${key}:${value}`)
          .join(',');

        updateFilterRaw('size', sizeParams);
      } else {
        updateFilterRaw('size', '');
      }
    },
    [updateFilterRaw]
  );

  const getButtonToMinMax = () => (
    <Button
      color="inherit"
      onClick={() => {
        setActiveType('minMax');
        // Clear plusMinus state when switching
        setPlusMinus({
          plusMinusWidth: '',
          plusMinusLength: '',
          widthTolerance: unitSystem === 'USC' ? 12 : 30,
          lengthTolerance: unitSystem === 'USC' ? 12 : 30,
        });
      }}
    >
      {sizesTranslations.minMaxButton}
    </Button>
  );

  const getButtonToPlusMinus = () => (
    <Button
      color="inherit"
      onClick={() => {
        setActiveType('plusMinus');
        // Clear minMax state when switching
        setMinMax({
          minWidth: '',
          maxWidth: '',
          minLength: '',
          maxLength: '',
        });
      }}
    >
      {sizesTranslations.toleranceButton}
    </Button>
  );

  const ToleranceChoice = (side) => (
    <FormControl id="tolerance" sx={{ width: '7ch' }}>
      <InputLabel id={`${side}Tolerance-label`}>{toleranceLabel}</InputLabel>
      <Select
        name={`${side}Tolerance`}
        labelId={`${side}Tolerance-label`}
        id={`${side}Tolerance`}
        value={plusMinus[`${side}Tolerance`]}
        onChange={handleSizeChange}
        label={toleranceLabel}
        inputProps={{ 'aria-label': 'Tolerance' }}
        variant="outlined"
      >
        {toleranceOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const handleSizeChange = (event) => {
    const { name, value } = event.target;

    // Check if the value is a valid number
    if (typeof value === 'string' && value !== '' && value.match(/[^\d.]/)) return;
    if (typeof value === 'number' && !Number.isFinite(value)) return;

    if (activeType === 'minMax') {
      // Type of search: min-max
      if (['minWidth', 'maxWidth', 'minLength', 'maxLength'].includes(name)) {
        const minMaxCopy = { ...minMax, [name]: value };
        setMinMax(minMaxCopy);

        // Skip processing for single-digit inputs (except for USC where it's valid)
        if (value.length === 1 && !(unitSystem === 'USC')) return;

        const size = {};
        if (minMaxCopy.maxWidth) size['maxWidth'] = convertToCm(minMaxCopy.maxWidth);
        if (minMaxCopy.minWidth) size['minWidth'] = convertToCm(minMaxCopy.minWidth);
        if (minMaxCopy.maxLength) size['maxLength'] = convertToCm(minMaxCopy.maxLength);
        if (minMaxCopy.minLength) size['minLength'] = convertToCm(minMaxCopy.minLength);

        updateSizeFilter(size);
      }
    } else if (activeType === 'plusMinus') {
      // Type of search: x +/-
      if (
        [
          'plusMinusWidth',
          'plusMinusLength',
          'widthTolerance',
          'lengthTolerance',
        ].includes(name)
      ) {
        const plusMinusCopy = { ...plusMinus, [name]: value };
        setPlusMinus(plusMinusCopy);

        // Skip processing for single-digit inputs (except for USC where it's valid)
        if (value.length === 1 && !name.includes('Tolerance') && !unitSystem === 'USC') return;

        const {
          plusMinusWidth,
          plusMinusLength,
          widthTolerance,
          lengthTolerance,
        } = plusMinusCopy;

        // For width: Convert base value and tolerance to cm first, then calculate min/max
        let widthInCm = convertToCm(plusMinusWidth);
        let widthToleranceInCm = convertToleranceToCm(widthTolerance);

        // For length: Convert base value and tolerance to cm first, then calculate min/max
        let lengthInCm = convertToCm(plusMinusLength);
        let lengthToleranceInCm = convertToleranceToCm(lengthTolerance);

        let maxWidth = widthInCm ? Number(widthInCm) + Number(widthToleranceInCm) : '';
        let minWidth = widthInCm ? Number(widthInCm) - Number(widthToleranceInCm) : '';
        let maxLength = lengthInCm ? Number(lengthInCm) + Number(lengthToleranceInCm) : '';
        let minLength = lengthInCm ? Number(lengthInCm) - Number(lengthToleranceInCm) : '';

        maxWidth = maxWidth < 0 ? '' : maxWidth;
        minWidth = minWidth < 0 ? '' : minWidth;
        maxLength = maxLength < 0 ? '' : maxLength;
        minLength = minLength < 0 ? '' : minLength;

        // Skip saving if the value is too small (less than 10cm) for SI, but allow small values for USC
        const minValueCheckRequired = unitSystem === 'SI';
        if (minValueCheckRequired &&
            ((minWidth !== '' && minWidth < 10) ||
             (minLength !== '' && minLength < 10))) {
          return;
        }

        const size = {};
        if (maxWidth) size['maxWidth'] = maxWidth;
        if (minWidth) size['minWidth'] = minWidth;
        if (maxLength) size['maxLength'] = maxLength;
        if (minLength) size['minLength'] = minLength;

        updateSizeFilter(size);
      }
    }
  };

  // Style for the fieldset label based on the presence of 'size' in the filters
  const labelStyle = {
    color: hasFilter('size') ? '#1976D2' : '',
  };

  return (
    <FormControl component="fieldset" aria-label={sizesTranslations.titleARIA} sx={{
      display: 'grid',
      justifyContent: 'center'
    }}>
      <FormLabel component="legend" style={labelStyle}>
        {SizesIcon}
        {sizesTranslations.title}
      </FormLabel>

      {(activeType === 'minMax' || activeType === 'plusMinus') && (
        <>
          {/* WIDTH */}
          <FormControl
            component="fieldset"
            aria-label={sizesTranslations.widthARIA}
          >
            <FormGroup>
              {activeType === 'minMax' ? (
                <Stack className="tc" marginY={1}>
                  <FormLabel component="legend">
                    {WidthIcon}
                    {sizesTranslations.width}
                  </FormLabel>
                  <TextField
                    type="search"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    step="any"
                    name="minWidth"
                    value={minMax.minWidth}
                    onChange={handleSizeChange}
                    label={sizesTranslations.widthMin}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{unitAdornment}</InputAdornment>,
                      },
                    }}
                  />
                  {sizesTranslations.separator}
                  <TextField
                    type="search"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    step="any"
                    name="maxWidth"
                    value={minMax.maxWidth}
                    onChange={handleSizeChange}
                    label={sizesTranslations.widthMax}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{unitAdornment}</InputAdornment>,
                      },
                    }}
                  />
                </Stack>
              ) : (
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs>
                    <TextField
                      fullWidth
                      type="search"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      step="any"
                      name="plusMinusLength"
                      value={plusMinus.plusMinusLength}
                      onChange={handleSizeChange}
                      label={sizesTranslations.length}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position="end">{unitAdornment}</InputAdornment>,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>{ToleranceChoice('length')}</Grid>
                </Grid>
              )}
            </FormGroup>
          </FormControl>

          {/* LENGTH */}
          <FormControl
            component="fieldset"
            aria-label={sizesTranslations.lengthARIA}
          >
            <FormGroup>
              {activeType === 'minMax' ? (
                <Stack className="tc" marginY={1}>
                  <FormLabel component="legend">
                    {LengthIcon}
                    {sizesTranslations.length}
                  </FormLabel>
                  <TextField
                    type="search"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    step="any"
                    name="minLength"
                    value={minMax.minLength}
                    onChange={handleSizeChange}
                    label={sizesTranslations.lengthMin}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{unitAdornment}</InputAdornment>,
                      },
                    }}
                  />
                  {sizesTranslations.separator}
                  <TextField
                    type="search"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    step="any"
                    name="maxLength"
                    value={minMax.maxLength}
                    onChange={handleSizeChange}
                    label={sizesTranslations.lengthMax}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{unitAdornment}</InputAdornment>,
                      },
                    }}
                  />
                </Stack>
              ) : (
                <Grid
                  container
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0, mb: 1 }}
                >
                  <Grid item xs>
                    <TextField
                      fullWidth
                      type="search"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      step="any"
                      name="plusMinusWidth"
                      value={plusMinus.plusMinusWidth}
                      onChange={handleSizeChange}
                      label={sizesTranslations.width}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position="end">{unitAdornment}</InputAdornment>,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>{ToleranceChoice('width')}</Grid>
                </Grid>
              )}
            </FormGroup>
          </FormControl>

          {/* Switch Buttons */}
          {activeType === 'minMax'
            ? getButtonToPlusMinus()
            : getButtonToMinMax()}
        </>
      )}
    </FormControl>
  );
}
